import { useCallback, useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { PageHeader, StatusPill } from "@/components/admin/PageHeader";
import { RequirePermission } from "@/components/admin/RequirePermission";
import {
  checkSocialAccountFn,
  disconnectSocialAccountFn,
  getSocialConfigFn,
  saveAiConfigFn,
  startMetaOAuthFn,
  testAiProviderFn,
} from "@/lib/social/social.functions";
import {
  AI_PROVIDERS,
  providerMeta,
  settingsFor,
  type AiConfig,
  type AiProviderId,
} from "@/lib/social/providers";
import { platformLabel, type SocialAccount, type SocialPlatform } from "@/lib/social/types";

export const Route = createFileRoute("/admin/ai-social")({
  head: () => ({
    meta: [
      { title: "AI & Social Integrations — CB16 Console" },
      { name: "description", content: "Configure AI providers and connect Facebook Pages and Instagram Business accounts." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: () => (
    <RequirePermission perm="settings.write">
      <AiSocialSettings />
    </RequirePermission>
  ),
});

const card = "rounded-sm border border-black/10 bg-white p-5";
const btn =
  "inline-flex items-center justify-center border border-black px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-black transition hover:border-[#C6A15B] hover:text-[#C6A15B] disabled:opacity-50";
const btnDark =
  "inline-flex items-center justify-center border border-black bg-black px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-white transition hover:border-[#C6A15B] hover:bg-[#C6A15B] disabled:opacity-50";
const input =
  "w-full border border-black/15 bg-white px-3 py-2 text-sm text-black focus:border-[#C6A15B] focus:outline-none";
const label = "block text-[10px] font-semibold uppercase tracking-[0.22em] text-black/50";

type ProviderStatus = {
  id: AiProviderId;
  label: string;
  secretName: string;
  connected: boolean;
  masked: string;
  supportsImage: boolean;
};

function AiSocialSettings() {
  const loadFn = useServerFn(getSocialConfigFn);
  const saveFn = useServerFn(saveAiConfigFn);
  const testFn = useServerFn(testAiProviderFn);
  const oauthFn = useServerFn(startMetaOAuthFn);
  const checkFn = useServerFn(checkSocialAccountFn);
  const disconnectFn = useServerFn(disconnectSocialAccountFn);

  const [config, setConfig] = useState<AiConfig | null>(null);
  const [providers, setProviders] = useState<ProviderStatus[]>([]);
  const [accounts, setAccounts] = useState<SocialAccount[]>([]);
  const [metaConfigured, setMetaConfigured] = useState(false);
  const [graphVersion, setGraphVersion] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = (await loadFn({ data: undefined } as never)) as {
        config: AiConfig;
        providers: ProviderStatus[];
        accounts: SocialAccount[];
        metaConfigured: boolean;
        graphVersion: string;
      };
      setConfig(res.config);
      setProviders(res.providers);
      setAccounts(res.accounts);
      setMetaConfigured(res.metaConfigured);
      setGraphVersion(res.graphVersion);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load settings.");
    } finally {
      setLoading(false);
    }
  }, [loadFn]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("connected")) setMessage("Meta account connected.");
    if (params.get("oauth_error")) setError(params.get("oauth_error"));
  }, []);

  async function run(key: string, fn: () => Promise<string | void>) {
    setBusy(key);
    setError(null);
    setMessage(null);
    try {
      const out = await fn();
      if (typeof out === "string") setMessage(out);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Action failed.");
    } finally {
      setBusy(null);
    }
  }

  function patch(id: AiProviderId, next: Partial<ReturnType<typeof settingsFor>>) {
    setConfig((c) =>
      c ? { ...c, providers: { ...c.providers, [id]: { ...settingsFor(c, id), ...next } } } : c,
    );
  }

  async function connect() {
    await run("oauth", async () => {
      const res = (await oauthFn({ data: { origin: window.location.origin } })) as { url: string };
      window.location.href = res.url;
    });
  }

  if (loading || !config) return <p className="py-10 text-sm text-black/50">Loading…</p>;

  const activeMeta = providerMeta(config.active);

  return (
    <div className="space-y-6">
      <PageHeader
        title="AI & Social Integrations"
        subtitle="AI providers for the Social Media Studio, plus Facebook Page and Instagram Business connections."
        action={
          <button
            type="button"
            className={btnDark}
            disabled={busy === "save"}
            onClick={() =>
              void run("save", async () => {
                await saveFn({ data: { config } });
                return "Settings saved.";
              })
            }
          >
            {busy === "save" ? "Saving…" : "Save settings"}
          </button>
        }
      />

      {error && <div className="rounded-sm border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>}
      {message && (
        <div className="rounded-sm border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">{message}</div>
      )}

      <section className={card}>
        <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-black">Active AI provider</h3>
        <p className="mt-1 text-xs text-black/50">
          API keys are never stored in the database — they live in Lovable Secrets and are only read server-side.
        </p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div>
            <label className={label} htmlFor="active-provider">Provider</label>
            <select
              id="active-provider"
              className={input}
              value={config.active}
              onChange={(e) => setConfig({ ...config, active: e.target.value as AiProviderId })}
            >
              {AI_PROVIDERS.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={label} htmlFor="active-model">Text model</label>
            <select
              id="active-model"
              className={input}
              value={settingsFor(config, config.active).model}
              onChange={(e) => patch(config.active, { model: e.target.value })}
            >
              {(activeMeta?.models ?? []).map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={label} htmlFor="active-temp">Temperature</label>
            <input
              id="active-temp"
              type="number"
              step="0.1"
              min="0"
              max="2"
              className={input}
              value={settingsFor(config, config.active).temperature}
              onChange={(e) => patch(config.active, { temperature: Number(e.target.value) })}
            />
          </div>
          <div>
            <label className={label} htmlFor="active-tokens">Max tokens</label>
            <input
              id="active-tokens"
              type="number"
              min="64"
              max="4000"
              className={input}
              value={settingsFor(config, config.active).maxTokens}
              onChange={(e) => patch(config.active, { maxTokens: Number(e.target.value) })}
            />
          </div>
          {activeMeta?.supportsImage && (
            <div>
              <label className={label} htmlFor="active-image">Image model</label>
              <select
                id="active-image"
                className={input}
                value={settingsFor(config, config.active).imageModel ?? ""}
                onChange={(e) => patch(config.active, { imageModel: e.target.value })}
              >
                {activeMeta.imageModels.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </section>

      <section className={card}>
        <h3 className="mb-4 text-sm font-semibold uppercase tracking-[0.2em] text-black">Provider credentials</h3>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead>
              <tr className="border-b border-black/10 text-[10px] uppercase tracking-[0.2em] text-black/50">
                <th className="py-2">Provider</th>
                <th className="py-2">Secret</th>
                <th className="py-2">Status</th>
                <th className="py-2">Image</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {providers.map((p) => (
                <tr key={p.id} className="border-b border-black/5">
                  <td className="py-3 font-medium text-black">{p.label}</td>
                  <td className="py-3 font-mono text-xs text-black/60">{p.secretName}</td>
                  <td className="py-3">
                    <StatusPill tone={p.connected ? "success" : "warn"}>
                      {p.connected ? `Connected ${p.masked}` : "Not configured"}
                    </StatusPill>
                  </td>
                  <td className="py-3 text-xs text-black/60">{p.supportsImage ? "Yes" : "—"}</td>
                  <td className="py-3 text-right">
                    <button
                      type="button"
                      className={btn}
                      disabled={busy === "test" + p.id || !p.connected}
                      onClick={() =>
                        void run("test" + p.id, async () => {
                          const res = (await testFn({ data: { provider: p.id } })) as {
                            ok: boolean;
                            message?: string;
                          };
                          return res.ok ? `${p.label} responded successfully.` : `${p.label}: ${res.message}`;
                        })
                      }
                    >
                      {busy === "test" + p.id ? "Testing…" : "Test"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs text-black/50">
          To enable a provider, add its secret in project settings. Ollama uses a base URL instead of a key.
        </p>
      </section>

      <section className={card}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-black">Connected accounts</h3>
            <p className="mt-1 text-xs text-black/50">
              Facebook Pages and Instagram Business accounts · Graph {graphVersion} ·{" "}
              {accounts.length === 0
                ? "No accounts connected"
                : accounts.map((a) => a.name).join(", ")}
            </p>
          </div>
          <button
            type="button"
            className={btnDark}
            disabled={busy === "oauth" || !metaConfigured}
            onClick={() => void connect()}
          >
            {busy === "oauth" ? "Redirecting…" : accounts.length > 0 ? "Reconnect with Meta" : "Connect with Meta"}
          </button>
        </div>

        {!metaConfigured && (
          <div className="mt-4 rounded-sm border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            Meta app credentials are missing. Add <code>META_APP_ID</code> and <code>META_APP_SECRET</code> as secrets,
            then add <code>{typeof window !== "undefined" ? window.location.origin : ""}/api/public/meta-oauth</code> as a
            valid OAuth redirect URI in your Meta app.
          </div>
        )}

        <div className="mt-5 grid gap-4 lg:grid-cols-2">
          {(["facebook", "instagram"] as SocialPlatform[]).map((platform) => {
            const list = accounts.filter((a) => a.platform === platform);
            return (
              <div key={platform} className="border border-black/10 p-4">
                <div className="flex items-center justify-between gap-3">
                  <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-black">
                    {platformLabel(platform)}
                  </h4>
                  <StatusPill tone={list.length > 0 ? "success" : "warn"}>
                    {list.length > 0 ? `${list.length} connected` : "Not connected"}
                  </StatusPill>
                </div>

                {list.length === 0 ? (
                  <>
                    <p className="mt-3 text-xs text-black/50">
                      {platform === "facebook"
                        ? "Authorise Meta and pick the Business and Page you post from. IDs are read from Meta — never typed in."
                        : "Authorise Meta and pick the Instagram Business account linked to your Page. IDs are read from Meta — never typed in."}
                    </p>
                    <button
                      type="button"
                      className={`${btn} mt-3`}
                      disabled={busy === "oauth" || !metaConfigured}
                      onClick={() => void connect()}
                    >
                      {platform === "facebook" ? "Connect Facebook" : "Connect Instagram Business"}
                    </button>
                  </>
                ) : (
                  <ul className="mt-3 space-y-4">
                    {list.map((a) => (
                      <li key={a.id} className="border-t border-black/5 pt-3 first:border-t-0 first:pt-0">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <p className="text-sm font-medium text-black">{a.name}</p>
                            {a.username && <p className="text-xs text-black/50">@{a.username}</p>}
                          </div>
                          <StatusPill tone={a.status === "connected" ? "success" : "danger"}>{a.status}</StatusPill>
                        </div>
                        <dl className="mt-3 grid gap-1 text-xs text-black/60">
                          <Row
                            k={platform === "facebook" ? "Page name" : "Business account"}
                            v={a.name}
                          />
                          <Row
                            k={platform === "facebook" ? "Page ID" : "Instagram ID"}
                            v={(platform === "facebook" ? a.pageId : a.igBusinessId) ?? a.externalId}
                          />
                          <Row k="Permissions" v={a.permissions.length > 0 ? a.permissions.join(", ") : "—"} />
                          {platform === "facebook" && (
                            <Row
                              k="Token expiry"
                              v={a.tokenExpiresAt ? new Date(a.tokenExpiresAt).toLocaleString() : "Long-lived (no expiry)"}
                            />
                          )}
                          <Row k="Last sync" v={a.lastSyncedAt ? new Date(a.lastSyncedAt).toLocaleString() : "—"} />
                        </dl>
                        {a.lastError && <p className="mt-2 text-xs text-red-700">{a.lastError}</p>}
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            className={btn}
                            disabled={busy === "check" + a.id}
                            onClick={() =>
                              void run("check" + a.id, async () => {
                                await checkFn({ data: { accountId: a.id } });
                                await load();
                                return "Connection re-checked.";
                              })
                            }
                          >
                            {busy === "check" + a.id ? "Checking…" : "Check status"}
                          </button>
                          <button
                            type="button"
                            className={btn}
                            disabled={busy === "oauth" || !metaConfigured}
                            onClick={() => void connect()}
                          >
                            Reconnect
                          </button>
                          <button
                            type="button"
                            className={btn}
                            disabled={busy === "dc" + a.id}
                            onClick={() =>
                              void run("dc" + a.id, async () => {
                                await disconnectFn({ data: { accountId: a.id } });
                                await load();
                                return "Account disconnected.";
                              })
                            }
                          >
                            Disconnect
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>

        <p className="mt-4 text-xs text-black/50">
          The Meta dialog is where you choose the Business, the Facebook Page and the linked Instagram Business account;
          everything authorised there is imported automatically and appears in the account pickers inside New Post.
        </p>
      </section>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex gap-2">
      <dt className="w-32 shrink-0 uppercase tracking-[0.14em] text-black/40">{k}</dt>
      <dd className="min-w-0 break-words text-black/70">{v}</dd>
    </div>
  );
}